import { Box, Button, Checkbox, Divider, FormControlLabel, FormHelperText, TextField } from "@mui/material";
import React, { useState } from "react";

import { OTHER_LABEL } from "config/LabelMapping";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";

import { DetectorQueryInput } from "./DetectorQueryInput";
import MultichoiceOptions, { ChoiceOption } from "./MultichoiceOptions";

const MAX_OPTIONS = 30;
const MAX_OPTION_LENGTH = 44;
const OTHERS_OPTION = { id: "others", text: OTHER_LABEL };

export interface CreateMultichoiceProps {
  queryValue: string;
  handleChangeValue: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setBeforeCreationHook: (callback: VoidFunction | null) => void;
  onOptionsCountChange?: (count: number) => void; // optional callback
  setHasErrors?: (hasErrors: boolean) => void;
}

export function CreateMultichoice(props: CreateMultichoiceProps) {
  const { queryValue, handleChangeValue, setBeforeCreationHook, onOptionsCountChange, setHasErrors } = props;

  const [options, setOptions] = React.useState<ChoiceOption[]>([]);
  const [newOption, setNewOption] = React.useState("");
  const [optionsError, setOptionsError] = React.useState("");
  const [includeOthers, setIncludeOthers] = React.useState(true);
  const [showAddClassNameOptionModal, setShowAddClassNameOptionModal] = useState(false);

  const onOptionsChange = (options: ChoiceOption[]) => {
    handleChangeValue({
      target: { name: "class_names", value: JSON.stringify(options?.map((option) => option.text)) },
    } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const handleOptionsChange = (options: ChoiceOption[]) => {
    if (options.length === 0) setOptionsError("Please add at least one option.");
    else setOptionsError("");

    setOptions(options);

    const modifiedOptions = [...options];
    if (includeOthers) {
      modifiedOptions.push(OTHERS_OPTION);
    }
    onOptionsChange(modifiedOptions);

    // Extract the current count
    const currentOptionsCount = options.length + (includeOthers ? 1 : 0);
    if (onOptionsCountChange) {
      onOptionsCountChange(currentOptionsCount);
    }

    // Optionally scroll into view
    const confidenceSlider = document.getElementsByClassName("MuiSlider-root")[0];
    if (confidenceSlider) {
      confidenceSlider.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  const handleIncludeOthers = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeOthers(event.target.checked);
    const modifiedOptions = [...options];

    if (event.target.checked) {
      modifiedOptions.push(OTHERS_OPTION);
    }

    // Extract the current count
    const currentOptionsCount = options.length + (event.target.checked ? 1 : 0);
    if (onOptionsCountChange) {
      onOptionsCountChange(currentOptionsCount);
    }

    onOptionsChange(modifiedOptions);
  };

  return (
    <>
      <DetectorQueryInput
        queryValue={queryValue}
        handleChangeValue={handleChangeValue}
        placeholder="Enter query here"
        setHasErrors={setHasErrors}
      />
      <Box>
        <MultichoiceOptions title="Answer Options" options={options} setOptions={handleOptionsChange} />
        <Box
          component="form"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (newOption === "" || options.length >= MAX_OPTIONS || optionsError !== "") return;
            handleOptionsChange([...options, { id: new Date().getTime().toString(), text: newOption }]);
            setNewOption("");
            setBeforeCreationHook(null);
          }}
        >
          <TextField
            sx={{ mt: options.length > 0 ? 2 : "0 !important", mb: "0 !important" }}
            className="text-sm md:text-base text-neutral-700 font-bold uppercase bg-white"
            type="text"
            name="newOption"
            size="small"
            placeholder="Create choice here"
            value={newOption}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue.length > MAX_OPTION_LENGTH) {
                setOptionsError(`Option length cannot exceed ${MAX_OPTION_LENGTH} characters.`);
                return;
              }
              setNewOption(inputValue);

              if (inputValue.trim() !== "") {
                setBeforeCreationHook(() => () => {
                  setShowAddClassNameOptionModal(true);
                });
              } else {
                setBeforeCreationHook(null);
              }

              if (options.find((option) => option.text === inputValue)) {
                setOptionsError("Option already exists.");
                return;
              }

              if (options.length >= MAX_OPTIONS) {
                setOptionsError("Maximum number of choices limit reached.");
                return;
              }

              setOptionsError("");
            }}
            error={optionsError !== ""}
            fullWidth
          />
          {optionsError && (
            <FormHelperText error sx={{ mt: 0 }}>
              {optionsError}
            </FormHelperText>
          )}
          <Box display="flex" justifyContent="end" mt={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setOptionsError("");
                handleOptionsChange([...options, { id: new Date().getTime().toString(), text: newOption }]);
                setNewOption("");
                setBeforeCreationHook(null);
              }}
              sx={{ px: 4 }}
              disabled={newOption === "" || options.length >= MAX_OPTIONS || optionsError !== ""}
            >
              Add
            </Button>
          </Box>

          <Box display="flex" justifyContent="start" alignItems="center" className="mt-0">
            <FormControlLabel
              control={<Checkbox checked={includeOthers} onChange={handleIncludeOthers} />}
              label='Enable "Other" field'
            />
          </Box>
          <Divider />
        </Box>
      </Box>

      <Modal
        size="small"
        open={showAddClassNameOptionModal}
        onClose={() => {
          setShowAddClassNameOptionModal(false);

          if (newOption.trim() !== "") {
            setBeforeCreationHook(() => () => {
              setShowAddClassNameOptionModal(true);
            });
          } else {
            setBeforeCreationHook(null);
          }
        }}
        onCancel={() => {
          setShowAddClassNameOptionModal(false);
          setNewOption("");
          setBeforeCreationHook(null);
        }}
        onConfirmation={() => {
          handleOptionsChange([...options, { id: new Date().getTime().toString(), text: newOption }]);
          setNewOption("");

          setShowAddClassNameOptionModal(false);
          setBeforeCreationHook(null);
        }}
        body={<Typography>Do you want to add “{newOption}” as an answer choice for this detector?</Typography>}
        confirmationButtonTitle="Yes"
        cancelButtonTitle="No"
      />
    </>
  );
}
